import { useCallback, useEffect, useMemo, useState } from "react";
import { useIso900114001DebouncedValue, useIso900114001LatestRequest } from "../../../components/shared/useIso900114001FilteredRequest";
import { getCalidadRiesgosOportunidades } from "../api/riesgos-oportunidades";
import type { RiesgosOportunidadesFilters, RiesgosOportunidadesResponse } from "../types/riesgos-oportunidades";

const emptyResponse: RiesgosOportunidadesResponse = {
  riesgos: [], oportunidades: [], control_modificaciones: [],
  meta: { years: [], current_year: new Date().getFullYear(), default_source_year: null, suggested_new_year: new Date().getFullYear(), riesgos_total: 0, oportunidades_total: 0 },
};

export function useCalidadRiesgosOportunidades(filters: RiesgosOportunidadesFilters, enabled = true) {
  const [data, setData] = useState<RiesgosOportunidadesResponse>(emptyResponse);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300);
  const { runLatest, hasActiveRequest } = useIso900114001LatestRequest();
  const requestFilters = useMemo<RiesgosOportunidadesFilters>(() => ({ ...filters, search: debouncedSearch }), [debouncedSearch, filters.anio]);
  const requestKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters]);

  const reload = useCallback(async (force = true) => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const response = await runLatest(requestKey, (signal) => getCalidadRiesgosOportunidades(requestFilters, signal), force);
      if (response) setData(response);
    } catch {
      setError("No se pudieron cargar los riesgos y oportunidades.");
    } finally {
      if (!hasActiveRequest()) setLoading(false);
    }
  }, [enabled, hasActiveRequest, requestFilters, requestKey, runLatest]);

  useEffect(() => { void reload(false); }, [reload]);

  return { ...data, loading, error, reload };
}
